import {
  ceAuthorizationInputSchema,
  classConfirmationInputSchema,
  classFindingInputSchema,
  endorsementInputSchema,
  onBoardIssuanceInputSchema,
  parseVesselSurveyInput,
  vesselCertificateInputSchema,
  vesselSurveyInputSchema,
} from '@felix/domain';
import type { z } from 'zod';

/**
 * Vessel Certificates formlarının şemaları (RHF + Zod; plan M7 Task 27a, 27b): `@felix/domain`'in
 * sunucuyla ortak şemaları ve gemi gününe göre "gelecek tarih yok" kuralı. Hata iletisi koddur
 * (`vesselCertificates.fieldErrors.<KOD>`); sunucu aynı kuralları yeniden uygular.
 */

type Ctx = z.RefinementCtx;

function notFuture(ctx: Ctx, path: string, value: string | null, today: string) {
  if (value !== null && value > today) {
    ctx.addIssue({ code: 'custom', message: 'FUTURE_DATE', path: [path] });
  }
}

/** Vize (gemi; K28): vize günü gemi gününden sonra olamaz. */
export function endorsementFormSchema(today: string) {
  return endorsementInputSchema.superRefine((value, ctx) =>
    notFuture(ctx, 'endorsedOn', value.endorsedOn, today),
  );
}

/** Sörveyör teyidi (gemi; K31): en az bir kalem; teyit günü gelecekte olamaz. */
export function confirmationFormSchema(today: string) {
  return classConfirmationInputSchema.superRefine((value, ctx) =>
    notFuture(ctx, 'confirmedOn', value.confirmedOn, today),
  );
}

/** Gemide düzenlenen sertifika (K28): düzenleme günü gelecekte olamaz; bitiş düzenlemeden sonra. */
export function issuanceFormSchema(today: string) {
  return onBoardIssuanceInputSchema.superRefine((value, ctx) =>
    notFuture(ctx, 'issueDate', value.issueDate, today),
  );
}

/** Sertifika (ofis): bitiş düzenlemeden önce olamaz; pencere üstüne yazma gerekçe ister (K29). */
export function certificateFormSchema(today: string) {
  return vesselCertificateInputSchema.superRefine((value, ctx) =>
    notFuture(ctx, 'issueDate', value.issueDate, today),
  );
}

/**
 * Survey Schedule satırı (ofis; K29): vade son sörveyden önce olamaz; hesaplanandan farklı pencere
 * gerekçe ister (`parseVesselSurveyInput` ile aynı kural, ADR 0029 D4).
 */
export const surveyFormSchema = vesselSurveyInputSchema.superRefine((value, ctx) => {
  const parsed = parseVesselSurveyInput(value);
  if (parsed.ok) return;
  for (const issue of parsed.issues) {
    ctx.addIssue({ code: 'custom', message: issue.code, path: [issue.path] });
  }
});

/** Klas bulgusu (ofis): verildiği ve kapandığı gün gelecekte olamaz. */
export function findingFormSchema(today: string) {
  return classFindingInputSchema.superRefine((value, ctx) => {
    notFuture(ctx, 'imposedOn', value.imposedOn, today);
    notFuture(ctx, 'closedOn', value.closedOn, today);
  });
}

/** Başmühendisin yetki beyanı (ofis; K31): bitiş başlangıçtan önce olamaz. */
export const authorizationFormSchema = ceAuthorizationInputSchema;

/** Şemanın ilk hatası alan yoluna göre (bileşen testleri ve alan hatası metni). */
export function fieldIssues(schema: z.ZodType, input: unknown): Record<string, string> | null {
  const result = schema.safeParse(input);
  if (result.success) return null;
  const issues: Record<string, string> = {};
  for (const issue of result.error.issues) {
    const path = issue.path.join('.');
    if (!(path in issues)) issues[path] = issue.message;
  }
  return issues;
}
